import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Baseline,
  Bold,
  BringToFront,
  Circle,
  Copy,
  Hash,
  ImagePlus,
  ImageUp,
  Italic,
  List,
  LoaderCircle,
  Minus,
  PaintBucket,
  PenLine,
  Redo2,
  RectangleHorizontal,
  SendToBack,
  Shapes,
  Square,
  Trash2,
  Type,
  Underline,
  Undo2,
} from 'lucide-react';
import {
  newSlideId,
  normalizeRotation,
  ROLE_FONT_SIZE,
  SLIDE_HEIGHT,
  SLIDE_WIDTH,
  TRAINING_FONTS,
  type Deck,
  type ImageElement,
  type Paragraph,
  type ShapeElement,
  type Slide,
  type SlideElement,
  type TextAlign,
  type TextElement,
  type TextRun,
  type TrainingFont,
} from '@allemnii-pulse/shared';
import { Button } from '../../../components/ui/Button.js';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/Popover.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/Select.js';
import { Textarea } from '../../../components/ui/Textarea.js';
import { useDeckFonts } from '../SlideView.js';
import { ColorInput, RotationInput, ToolbarDivider, ToolButton } from './controls.js';
import type { FormatShortcut } from './EditableText.js';
import {
  expandToWord,
  fullRange,
  isCollapsed,
  mapParagraphsInRange,
  mapRunsInRange,
  runsInRange,
  type TextRange,
} from './richText.js';
import { SlideCanvas, type Box } from './SlideCanvas.js';
import { SlideRail } from './SlideRail.js';
import { useDeckHistory } from './useDeckHistory.js';

const FONT_SIZES = [16, 20, 24, 28, 32, 36, 40, 44, 48, 56, 64, 72, 80, 96, 108, 128, 160, 200];
const STROKE_WIDTHS = [0, 2, 4, 6, 8, 12, 16, 24];
const NUDGE = 5;
const NUDGE_FAST = 40;

type InlineToggle = 'bold' | 'italic' | 'underline';
type ShapeKind = ShapeElement['shape'];

/** Deep-copies a slide element under a new id (used by duplicate). */
function cloneElement(el: SlideElement, offset = 0): SlideElement {
  return { ...structuredClone(el), id: newSlideId(el.type), x: el.x + offset, y: el.y + offset };
}

/** Whether a run renders bold — titles are bold unless a run says otherwise. */
function isBold(run: TextRun, el: TextElement): boolean {
  return run.bold ?? el.role === 'title';
}

/** The value all `values` share, or null when they differ (or there are none). */
function common<T>(values: T[]): T | null {
  if (values.length === 0) return null;
  return values.every((v) => v === values[0]) ? values[0]! : null;
}

/**
 * The slide editor: a controlled deck in, every committed edit out through onChange (the parent
 * autosaves it as a draft and saves versions explicitly). Elements are moved and resized on a
 * 1920x1080 canvas scaled to fit; text is edited in place (EditableText) with all formatting
 * applied to the Paragraph/TextRun model by character offsets, so nothing but the model is ever
 * stored. Undo/redo history lives here and spans every kind of edit.
 */
export function SlideEditor({
  deck,
  onChange,
  imageUrls,
  onUploadImage,
}: {
  deck: Deck;
  onChange: (deck: Deck) => void;
  imageUrls: Record<string, string>;
  onUploadImage: (file: File) => Promise<string>;
}) {
  const history = useDeckHistory(deck, onChange);
  const { commit, current } = history;
  const design = deck.design;
  useDeckFonts(useMemo(() => [deck], [deck]));

  const [slideIndex, setSlideIndex] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPoint, setEditPoint] = useState<{ x: number; y: number } | null>(null);
  const [textSel, setTextSel] = useState<TextRange | null>(null);
  const selectionRef = useRef<TextRange | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTarget = useRef<{ slideId: string; elementId: string | null } | null>(null);

  const index = Math.min(slideIndex, deck.slides.length - 1);
  const slide = deck.slides[index]!;
  const selected = slide.elements.find((el) => el.id === selectedId) ?? null;
  const editing = selected && editingId === selected.id ? selected : null;

  // ---- Canvas sizing ----
  const hostRef = useRef<HTMLDivElement>(null);
  const [canvasWidth, setCanvasWidth] = useState(960);
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const observer = new ResizeObserver(([entry]) => {
      const available = entry?.contentRect.width ?? 960;
      // Leave room below for the toolbar and speaker notes on a laptop screen. A phone's editor
      // scrolls as one page instead, so there the slide just fills the width — and doesn't
      // shrink when the on-screen keyboard takes half the height.
      const maxByHeight = window.matchMedia('(min-width: 768px)').matches
        ? Math.max(320, (window.innerHeight - 330) * (SLIDE_WIDTH / SLIDE_HEIGHT))
        : Infinity;
      setCanvasWidth(Math.max(240, Math.floor(Math.min(available, maxByHeight))));
    });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  // ---- Model helpers (always built on the latest committed deck) ----

  const updateSlide = useCallback(
    (slideId: string, fn: (s: Slide) => Slide, coalesceKey?: string) => {
      const d = current();
      commit({ ...d, slides: d.slides.map((s) => (s.id === slideId ? fn(s) : s)) }, coalesceKey);
    },
    [commit, current],
  );

  const updateElement = useCallback(
    (elementId: string, fn: (el: SlideElement) => SlideElement, coalesceKey?: string) => {
      updateSlide(
        slide.id,
        (s) => ({ ...s, elements: s.elements.map((el) => (el.id === elementId ? fn(el) : el)) }),
        coalesceKey,
      );
    },
    [slide.id, updateSlide],
  );

  function selectSlide(i: number) {
    setEditingId(null);
    setSelectedId(null);
    setSlideIndex(i);
  }

  function stopEditing() {
    setEditingId(null);
    setTextSel(null);
    selectionRef.current = null;
  }

  // ---- Slides ----

  function addSlide() {
    const d = current();
    const blank: Slide = {
      id: newSlideId('slide'),
      background: design.backgroundColor,
      speakerNotes: '',
      elements: [
        {
          id: newSlideId('text'),
          type: 'text',
          role: 'title',
          x: 120,
          y: 90,
          w: 1680,
          h: 160,
          verticalAlign: 'bottom',
          paragraphs: [{ runs: [{ text: 'New slide' }] }],
        },
      ],
    };
    const at = index + 1;
    commit({ ...d, slides: [...d.slides.slice(0, at), blank, ...d.slides.slice(at)] });
    selectSlide(at);
  }

  function duplicateSlide() {
    const d = current();
    const copy: Slide = {
      ...structuredClone(slide),
      id: newSlideId('slide'),
      elements: slide.elements.map((el) => cloneElement(el)),
    };
    const at = index + 1;
    commit({ ...d, slides: [...d.slides.slice(0, at), copy, ...d.slides.slice(at)] });
    selectSlide(at);
  }

  function deleteSlide() {
    const d = current();
    if (d.slides.length <= 1) return;
    commit({ ...d, slides: d.slides.filter((s) => s.id !== slide.id) });
    selectSlide(Math.max(0, index - 1));
  }

  function moveSlide(from: number, to: number) {
    const d = current();
    if (to < 0 || to >= d.slides.length || from === to) return;
    const slides = [...d.slides];
    const [moved] = slides.splice(from, 1);
    slides.splice(to, 0, moved!);
    commit({ ...d, slides });
    selectSlide(to);
  }

  // ---- Elements ----

  function addElement(el: SlideElement) {
    updateSlide(slide.id, (s) => ({ ...s, elements: [...s.elements, el] }));
    setEditingId(null);
    setSelectedId(el.id);
  }

  function addTextBox() {
    addElement({
      id: newSlideId('text'),
      type: 'text',
      role: 'body',
      x: 360,
      y: 440,
      w: 1200,
      h: 200,
      verticalAlign: 'top',
      paragraphs: [{ runs: [{ text: 'Text' }] }],
    });
  }

  function addShape(shape: ShapeKind) {
    const line = shape === 'line';
    addElement({
      id: newSlideId('shape'),
      type: 'shape',
      shape,
      x: line ? 660 : 760,
      y: line ? 530 : 390,
      w: line ? 600 : 400,
      h: line ? 20 : 300,
      fill: line ? null : design.primaryColor,
      stroke: line ? design.accentColor : null,
      strokeWidth: line ? 6 : 0,
    });
  }

  function deleteSelected() {
    if (!selected) return;
    updateSlide(slide.id, (s) => ({
      ...s,
      elements: s.elements.filter((el) => el.id !== selected.id),
    }));
    setSelectedId(null);
    stopEditing();
  }

  function duplicateSelected() {
    if (!selected) return;
    const copy = cloneElement(selected, 30);
    updateSlide(slide.id, (s) => ({ ...s, elements: [...s.elements, copy] }));
    setSelectedId(copy.id);
    stopEditing();
  }

  /** Moves the selected element one step up (+1) or down (-1) the paint order. */
  function reorder(direction: 1 | -1) {
    if (!selected) return;
    updateSlide(slide.id, (s) => {
      const elements = [...s.elements];
      const i = elements.findIndex((el) => el.id === selected.id);
      const j = i + direction;
      if (i === -1 || j < 0 || j >= elements.length) return s;
      [elements[i], elements[j]] = [elements[j]!, elements[i]!];
      return { ...s, elements };
    });
  }

  function commitBox(id: string, box: Box) {
    updateElement(id, (el) => ({ ...el, ...box }));
  }

  /** From the toolbar's degree field — consecutive keystrokes are one undo step. */
  function setRotation(id: string, degrees: number) {
    updateElement(id, (el) => ({ ...el, rotation: normalizeRotation(degrees) }), `rotate-${id}`);
  }

  // ---- Images ----

  function requestUpload(elementId: string | null) {
    uploadTarget.current = { slideId: slide.id, elementId };
    fileInputRef.current?.click();
  }

  async function handleFile(file: File) {
    const target = uploadTarget.current;
    uploadTarget.current = null;
    if (!target) return;
    setUploading(true);
    setError(null);
    try {
      const storagePath = await onUploadImage(file);
      if (target.elementId) {
        const elementId = target.elementId;
        updateSlide(target.slideId, (s) => ({
          ...s,
          elements: s.elements.map((el) =>
            el.id === elementId && el.type === 'image' ? { ...el, storagePath } : el,
          ),
        }));
        return;
      }
      // A new image keeps its aspect ratio, fitted into the middle of the slide.
      let ratio = 4 / 3;
      try {
        const bitmap = await createImageBitmap(file);
        ratio = bitmap.width / bitmap.height || ratio;
        bitmap.close();
      } catch {
        // Fall back to 4:3 if the browser can't decode it here.
      }
      const w = Math.round(Math.min(1000, 700 * ratio));
      const h = Math.round(w / ratio);
      const image: ImageElement = {
        id: newSlideId('image'),
        type: 'image',
        storagePath,
        fit: 'contain',
        x: Math.round((SLIDE_WIDTH - w) / 2),
        y: Math.round((SLIDE_HEIGHT - h) / 2),
        w,
        h,
      };
      updateSlide(target.slideId, (s) => ({ ...s, elements: [...s.elements, image] }));
      setSelectedId(image.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not upload the image.');
    } finally {
      setUploading(false);
    }
  }

  // ---- Text formatting ----

  /** The selected text element, the range formatting applies to, and the range inline styles
   * apply to: the text selection while editing (a bare caret widens to its word for inline
   * styles), or the whole element when it's only selected. */
  function formatTarget(): { el: TextElement; range: TextRange; inline: TextRange } | null {
    if (!selected || selected.type !== 'text') return null;
    const sel = editing ? selectionRef.current : null;
    if (!sel) {
      const all = fullRange(selected.paragraphs);
      return { el: selected, range: all, inline: all };
    }
    return {
      el: selected,
      range: sel,
      inline: isCollapsed(sel) ? expandToWord(selected.paragraphs, sel.start) : sel,
    };
  }

  function setParagraphs(elementId: string, paragraphs: Paragraph[], coalesce = false) {
    updateElement(
      elementId,
      (el) => (el.type === 'text' ? { ...el, paragraphs } : el),
      coalesce ? `type-${elementId}` : undefined,
    );
  }

  function toggleInline(key: InlineToggle) {
    const t = formatTarget();
    if (!t) return;
    const runs = runsInRange(t.el.paragraphs, t.inline);
    const on = (r: TextRun) => (key === 'bold' ? isBold(r, t.el) : !!r[key]);
    const value = !(runs.length > 0 && runs.every(on));
    // Titles are bold by default, so un-bolding one has to be stored explicitly.
    const stored = value ? true : key === 'bold' && t.el.role === 'title' ? false : undefined;
    setParagraphs(
      t.el.id,
      mapRunsInRange(t.el.paragraphs, t.inline, (r) => ({ ...r, [key]: stored })),
    );
  }

  function setRunField<K extends 'fontSize' | 'fontFamily' | 'color'>(key: K, value: TextRun[K]) {
    const t = formatTarget();
    if (!t) return;
    setParagraphs(
      t.el.id,
      mapRunsInRange(t.el.paragraphs, t.inline, (r) => ({ ...r, [key]: value })),
    );
  }

  function setAlign(align: TextAlign) {
    const t = formatTarget();
    if (!t) return;
    setParagraphs(
      t.el.id,
      mapParagraphsInRange(t.el.paragraphs, t.range, (p) => ({ ...p, align })),
    );
  }

  function toggleBullet() {
    const t = formatTarget();
    if (!t) return;
    const all = t.el.paragraphs.slice(t.range.start.p, t.range.end.p + 1).every((p) => p.bullet);
    setParagraphs(
      t.el.id,
      mapParagraphsInRange(t.el.paragraphs, t.range, (p) => ({
        ...p,
        bullet: all ? undefined : true,
      })),
    );
  }

  function formatShortcut(kind: FormatShortcut) {
    toggleInline(kind);
  }

  /** What the toolbar shows for the current text target. */
  const textFormat = useMemo(() => {
    if (!selected || selected.type !== 'text') return null;
    const sel = editing ? textSel : null;
    const range = sel ?? fullRange(selected.paragraphs);
    const inline = sel && isCollapsed(sel) ? sel : range;
    const runs = runsInRange(selected.paragraphs, inline);
    const role = selected.role;
    const defaultFont: TrainingFont = role === 'title' ? design.headingFont : design.bodyFont;
    const defaultColor = role === 'title' ? design.primaryColor : design.textColor;
    const paras = selected.paragraphs.slice(range.start.p, range.end.p + 1);
    return {
      bold: runs.length > 0 && runs.every((r) => isBold(r, selected)),
      italic: runs.length > 0 && runs.every((r) => !!r.italic),
      underline: runs.length > 0 && runs.every((r) => !!r.underline),
      fontSize: common(runs.map((r) => r.fontSize ?? ROLE_FONT_SIZE[role])),
      fontFamily: common(runs.map((r) => r.fontFamily ?? defaultFont)),
      color: common(runs.map((r) => r.color ?? defaultColor)),
      align: common(paras.map((p) => p.align ?? 'left')),
      bullet: paras.length > 0 && paras.every((p) => p.bullet),
    };
  }, [selected, editing, textSel, design]);

  // Toolbar selects and color pickers take focus; put it back in the text being edited.
  function refocusEditing() {
    if (!editing) return;
    requestAnimationFrame(() => {
      const el = document.activeElement;
      if (!el || !(el as HTMLElement).isContentEditable) {
        const editable = hostRef.current?.querySelector<HTMLElement>('[contenteditable="true"]');
        editable?.focus({ preventScroll: true });
      }
    });
  }

  // ---- Keyboard ----

  function handleKeyDown(e: ReactKeyboardEvent) {
    const target = e.target as HTMLElement;
    if (target.closest('input, textarea, select, [contenteditable="true"]')) return;
    const mod = e.metaKey || e.ctrlKey;
    const key = e.key.toLowerCase();
    if (mod && (key === 'z' || key === 'y')) {
      e.preventDefault();
      if (key === 'y' || e.shiftKey) history.redo();
      else history.undo();
      return;
    }
    if (!selected) return;
    if (mod && key === 'd') {
      e.preventDefault();
      duplicateSelected();
      return;
    }
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      deleteSelected();
      return;
    }
    if (e.key === 'Escape') {
      setSelectedId(null);
      return;
    }
    if (e.key === 'Enter' && selected.type === 'text') {
      e.preventDefault();
      setEditPoint(null);
      setEditingId(selected.id);
      return;
    }
    const step = e.shiftKey ? NUDGE_FAST : NUDGE;
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const d = delta[e.key];
    if (d) {
      e.preventDefault();
      updateElement(
        selected.id,
        (el) => ({ ...el, x: el.x + d[0], y: el.y + d[1] }),
        `nudge-${selected.id}`,
      );
    }
  }

  // ---- Render ----

  const selectedShape = selected?.type === 'shape' ? selected : null;
  const selectedImage = selected?.type === 'image' ? selected : null;
  const fontSizes =
    textFormat?.fontSize && !FONT_SIZES.includes(textFormat.fontSize)
      ? [...FONT_SIZES, textFormat.fontSize].sort((a, b) => a - b)
      : FONT_SIZES;

  return (
    // Fills the page's height so the slide rail scrolls on its own instead of the whole page. On a
    // phone it's one scrolling column instead: toolbar, slide strip, canvas, notes.
    <div className="flex flex-col gap-3 md:h-full md:min-h-0" onKeyDown={handleKeyDown}>
      {/* Toolbar. On a phone it's a single swipeable row pinned to the top while the page
          scrolls, so formatting stays in reach of the text being edited. */}
      <div className="flex flex-wrap items-center gap-0.5 rounded-lg border border-border bg-surface px-1.5 py-1 max-md:sticky max-md:top-0 max-md:z-10 max-md:flex-nowrap max-md:overflow-x-auto max-md:*:shrink-0">
        <ToolButton label="Undo (Ctrl+Z)" disabled={!history.canUndo} onClick={history.undo}>
          <Undo2 />
        </ToolButton>
        <ToolButton label="Redo (Ctrl+Shift+Z)" disabled={!history.canRedo} onClick={history.redo}>
          <Redo2 />
        </ToolButton>
        <ToolbarDivider />

        <ToolButton label="Add text box" onClick={addTextBox}>
          <Type />
        </ToolButton>
        <ToolButton label="Add image" disabled={uploading} onClick={() => requestUpload(null)}>
          {uploading ? <LoaderCircle className="animate-spin" /> : <ImagePlus />}
        </ToolButton>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              title="Add shape"
              aria-label="Add shape"
              className="h-10 w-10 md:h-8 md:w-8"
            >
              <Shapes />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="flex gap-0.5 p-1">
            <ToolButton label="Rectangle" onClick={() => addShape('rect')}>
              <Square />
            </ToolButton>
            <ToolButton label="Rounded rectangle" onClick={() => addShape('roundRect')}>
              <RectangleHorizontal />
            </ToolButton>
            <ToolButton label="Ellipse" onClick={() => addShape('ellipse')}>
              <Circle />
            </ToolButton>
            <ToolButton label="Line" onClick={() => addShape('line')}>
              <Minus />
            </ToolButton>
          </PopoverContent>
        </Popover>
        <ColorInput
          label="Slide background"
          icon={<PaintBucket className="h-4 w-4" />}
          value={slide.background}
          onChange={(color) => updateSlide(slide.id, (s) => ({ ...s, background: color }))}
        />
        <ToolButton
          label={slide.showNumber ? 'Hide slide number' : 'Show slide number'}
          active={!!slide.showNumber}
          onClick={() => updateSlide(slide.id, (s) => ({ ...s, showNumber: !s.showNumber }))}
        >
          <Hash />
        </ToolButton>

        {textFormat && selected?.type === 'text' && (
          <>
            <ToolbarDivider />
            <Select
              value={textFormat.fontFamily ?? ''}
              onValueChange={(font) => {
                setRunField('fontFamily', font as TrainingFont);
                refocusEditing();
              }}
            >
              <SelectTrigger className="h-8 w-40" aria-label="Font" title="Font">
                <SelectValue placeholder="Font" />
              </SelectTrigger>
              <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                {TRAINING_FONTS.map((font) => (
                  <SelectItem key={font} value={font}>
                    <span style={{ fontFamily: `'${font}'` }}>{font}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={textFormat.fontSize ? String(textFormat.fontSize) : ''}
              onValueChange={(size) => {
                setRunField('fontSize', Number(size));
                refocusEditing();
              }}
            >
              <SelectTrigger className="h-8 w-20" aria-label="Font size" title="Font size">
                <SelectValue placeholder="Size" />
              </SelectTrigger>
              <SelectContent onCloseAutoFocus={(e) => e.preventDefault()}>
                {fontSizes.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <ToolButton
              label="Bold (Ctrl+B)"
              active={textFormat.bold}
              onClick={() => toggleInline('bold')}
            >
              <Bold />
            </ToolButton>
            <ToolButton
              label="Italic (Ctrl+I)"
              active={textFormat.italic}
              onClick={() => toggleInline('italic')}
            >
              <Italic />
            </ToolButton>
            <ToolButton
              label="Underline (Ctrl+U)"
              active={textFormat.underline}
              onClick={() => toggleInline('underline')}
            >
              <Underline />
            </ToolButton>
            <ColorInput
              label="Text color"
              icon={<Baseline className="h-4 w-4" />}
              value={textFormat.color}
              onChange={(color) => {
                setRunField('color', color);
                refocusEditing();
              }}
            />
            {(
              [
                ['left', AlignLeft],
                ['center', AlignCenter],
                ['right', AlignRight],
                ['justify', AlignJustify],
              ] as const
            ).map(([align, Icon]) => (
              <ToolButton
                key={align}
                label={`Align ${align}`}
                active={textFormat.align === align}
                onClick={() => setAlign(align)}
              >
                <Icon />
              </ToolButton>
            ))}
            <ToolButton label="Bullets" active={textFormat.bullet} onClick={toggleBullet}>
              <List />
            </ToolButton>
            {!editing && (
              <ToolButton
                label="Edit text (Enter, or double-click)"
                onClick={() => {
                  setEditPoint(null);
                  setEditingId(selected.id);
                }}
              >
                <PenLine />
              </ToolButton>
            )}
          </>
        )}

        {selectedImage && (
          <>
            <ToolbarDivider />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={uploading}
              onClick={() => requestUpload(selectedImage.id)}
            >
              <ImageUp />
              {selectedImage.storagePath ? 'Replace image' : 'Upload image'}
            </Button>
            <Select
              value={selectedImage.fit}
              onValueChange={(fit) =>
                updateElement(selectedImage.id, (el) =>
                  el.type === 'image' ? { ...el, fit: fit as ImageElement['fit'] } : el,
                )
              }
            >
              <SelectTrigger className="h-8 w-28" aria-label="Image fit" title="Image fit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="contain">Fit</SelectItem>
                <SelectItem value="cover">Fill (crop)</SelectItem>
              </SelectContent>
            </Select>
          </>
        )}

        {selectedShape && (
          <>
            <ToolbarDivider />
            {selectedShape.shape !== 'line' && (
              <>
                <ColorInput
                  label="Fill color"
                  icon={<PaintBucket className="h-4 w-4" />}
                  value={selectedShape.fill}
                  onChange={(fill) =>
                    updateElement(selectedShape.id, (el) =>
                      el.type === 'shape' ? { ...el, fill } : el,
                    )
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={!selectedShape.fill}
                  onClick={() =>
                    updateElement(selectedShape.id, (el) =>
                      el.type === 'shape' ? { ...el, fill: null } : el,
                    )
                  }
                >
                  No fill
                </Button>
              </>
            )}
            <ColorInput
              label={selectedShape.shape === 'line' ? 'Line color' : 'Border color'}
              icon={<PenLine className="h-4 w-4" />}
              value={selectedShape.stroke}
              onChange={(stroke) =>
                updateElement(selectedShape.id, (el) =>
                  el.type === 'shape' ? { ...el, stroke, strokeWidth: el.strokeWidth || 4 } : el,
                )
              }
            />
            <Select
              value={String(selectedShape.strokeWidth)}
              onValueChange={(w) =>
                updateElement(selectedShape.id, (el) =>
                  el.type === 'shape' ? { ...el, strokeWidth: Number(w) } : el,
                )
              }
            >
              <SelectTrigger
                className="h-8 w-24"
                aria-label={selectedShape.shape === 'line' ? 'Line width' : 'Border width'}
                title={selectedShape.shape === 'line' ? 'Line width' : 'Border width'}
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[...new Set([...STROKE_WIDTHS, selectedShape.strokeWidth])]
                  .sort((a, b) => a - b)
                  .map((w) => (
                    <SelectItem key={w} value={String(w)}>
                      {w === 0 ? 'None' : `${w}px`}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </>
        )}

        {selected && (
          <>
            <ToolbarDivider />
            <RotationInput
              value={selected.rotation ?? 0}
              onChange={(degrees) => setRotation(selected.id, degrees)}
            />
            <ToolButton label="Bring forward" onClick={() => reorder(1)}>
              <BringToFront />
            </ToolButton>
            <ToolButton label="Send backward" onClick={() => reorder(-1)}>
              <SendToBack />
            </ToolButton>
            <ToolButton label="Duplicate (Ctrl+D)" onClick={duplicateSelected}>
              <Copy />
            </ToolButton>
            <ToolButton label="Delete (Del)" onClick={deleteSelected}>
              <Trash2 />
            </ToolButton>
          </>
        )}
      </div>

      {error && <p className="text-sm text-danger">{error}</p>}

      <div className="flex flex-col gap-3 md:min-h-0 md:flex-1 md:flex-row">
        <SlideRail
          deck={deck}
          imageUrls={imageUrls}
          current={index}
          onSelect={selectSlide}
          onAdd={addSlide}
          onDuplicate={duplicateSlide}
          onDelete={deleteSlide}
          onMove={moveSlide}
        />

        <div className="flex min-w-0 flex-col gap-3 md:min-h-0 md:flex-1 md:overflow-y-auto">
          <div ref={hostRef} className="w-full">
            <SlideCanvas
              slide={slide}
              design={design}
              imageUrls={imageUrls}
              width={canvasWidth}
              slideNumber={index + 1}
              selectedId={selected?.id ?? null}
              editingId={editing?.id ?? null}
              editPoint={editPoint}
              selectionRef={selectionRef}
              onSelect={setSelectedId}
              onStartEditing={(id, point) => {
                setSelectedId(id);
                setEditPoint(point);
                setEditingId(id);
              }}
              onStopEditing={stopEditing}
              onCommitBox={commitBox}
              onParagraphs={(id, paragraphs, coalesce) => setParagraphs(id, paragraphs, coalesce)}
              onTextSelection={setTextSel}
              onRequestUpload={requestUpload}
              onUndo={history.undo}
              onRedo={history.redo}
              onFormatShortcut={formatShortcut}
            />
          </div>
          <div className="flex flex-col gap-1.5" style={{ maxWidth: canvasWidth }}>
            <label htmlFor="slide-speaker-notes" className="text-xs font-medium text-muted">
              Speaker notes
            </label>
            <Textarea
              id="slide-speaker-notes"
              rows={4}
              maxLength={10_000}
              value={slide.speakerNotes}
              placeholder="What the trainer says on this slide…"
              onChange={(e) => {
                const speakerNotes = e.target.value;
                updateSlide(slide.id, (s) => ({ ...s, speakerNotes }), `notes-${slide.id}`);
              }}
            />
          </div>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void handleFile(file);
          else uploadTarget.current = null;
        }}
      />
    </div>
  );
}
